import React, { useRef, useState } from 'react';
import { Button, FormInput, FormSelect } from '@getro/rombo';
import { ChevronDown } from 'lucide-react';
import { Field, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { Dropdown } from 'components/molecules/dropdown';
import { monthOptions } from './date';

const TriggerContainer = styled.span`
  display: flex;
  align-items: center;
  font-size: 12px;
  cursor: pointer;
  gap: 4px;
  color: ${({ theme }) => theme.colors.text.subtle};
`;

const DropdownContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.neutral[0]};
  border-radius: 4px;
  padding: 16px;
  width: 232px;
  box-shadow: ${({ theme }) => theme.shadows.medium};
`;

const DateLabel = styled.span`
  flex: 0 0 auto;
  width: 40px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.subtle};
`;

const TextError = styled.p`
  font-size: 12px;
  margin: 0;
  color: ${({ theme }) => theme.colors.text.error};
`;

const BottomText = styled.p`
  font-size: 12px;
  margin: 0;
  font-style: italic;
  color: ${({ theme }) => theme.colors.text.subtle};
`;

export const PeriodSelector = ({ selected, onSelect, bottomText }) => {
  const dropdownRef = useRef(null);
  const [errorText, setErrorText] = useState(false);
  const initialValues = {
    fromMonth: monthOptions()[0],
    fromYear: 2010,
    toMonth: monthOptions()[0],
    toYear: 2020,
  };

  const onSubmit = (values) => {
    const { fromYear, toYear } = values;
    const numericFromYear = fromYear ? parseInt(fromYear, 10) : 0;
    const numericToYear = toYear ? parseInt(toYear, 10) : 0;

    setErrorText(false);
    if (
      Number.isNaN(numericFromYear) ||
      Number.isNaN(numericToYear) ||
      !numericFromYear ||
      numericFromYear < 1 ||
      !numericToYear ||
      numericToYear < 1 ||
      numericFromYear > numericToYear ||
      (numericFromYear === numericToYear && values.fromMonth?.value > values.toMonth?.value) ||
      numericFromYear < 1000 ||
      numericToYear < 1000
    ) {
      setErrorText(true);
    } else {
      onSelect({ ...values, fromYear: numericFromYear, toYear: numericToYear });
      dropdownRef.current?.closeDropdown();
    }
  };

  return (
    <div>
      <Dropdown
        ref={dropdownRef}
        placement="bottom-start"
        zIndex={300}
        trigger={
          <TriggerContainer>
            <span>
              {selected
                ? `${selected.fromMonth.label} ${selected.fromYear} - ${selected.toMonth.label} ${selected.toYear}`
                : `${initialValues.fromMonth.label} ${initialValues.fromYear} - ${initialValues.toMonth.label} ${initialValues.toYear}`}
            </span>
            <ChevronDown size={16} />
          </TriggerContainer>
        }
      >
        <DropdownContainer>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Formik initialValues={selected || initialValues} onSubmit={onSubmit} enableReinitialize>
              <Form>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <DateLabel>From</DateLabel>
                    <Field
                      name="fromMonth"
                      component={FormSelect}
                      size="small"
                      options={monthOptions()}
                      containerProps={{ flex: 1 }}
                    />
                    <Field
                      name="fromYear"
                      component={FormInput}
                      size="small"
                      placeholder="2010"
                      containerProps={{ flex: 1 }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <DateLabel>To</DateLabel>
                    <Field
                      name="toMonth"
                      component={FormSelect}
                      size="small"
                      options={monthOptions()}
                      containerProps={{ flex: 1 }}
                    />
                    <Field
                      name="toYear"
                      component={FormInput}
                      size="small"
                      placeholder="2020"
                      containerProps={{ flex: 1 }}
                    />
                  </div>
                  <Button
                    size="small"
                    type="submit"
                    sx={{
                      width: '100%',
                      justifyContent: 'center',
                    }}
                  >
                    Apply
                  </Button>
                </div>
              </Form>
            </Formik>
            {bottomText && <BottomText>{bottomText}</BottomText>}
            {errorText && <TextError>{`Enter a valid year for "From" and "To" dates.`}</TextError>}
          </div>
        </DropdownContainer>
      </Dropdown>
    </div>
  );
};

PeriodSelector.propTypes = {
  selected: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
  bottomText: PropTypes.node,
};

PeriodSelector.defaultProps = {
  bottomText: null,
  selected: null,
};
